import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles, getHotArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article.id) : [];
  const hotArticles = getHotArticles().slice(0, 4);

  if (!article) {
    return (
      <>
        <main>
          <Navbar />
          <section className="pt-20 pb-20 bg-background-50 min-h-screen flex items-center justify-center">
            <div className="text-center">
              <p className="text-lg text-foreground-500 mb-4">文章不存在或已被删除</p>
              <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                返回首页
              </Link>
            </div>
          </section>
          <Footer />
        </main>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '51吃瓜网',
    },
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜网',
      url: 'https://51chigua.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://51chigua.com/article/${article.slug}`,
    },
    image: article.image,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51chigua.com/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://51chigua.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <title>{article.title} - 51吃瓜网</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <link rel="canonical" href={`https://51chigua.com/article/${article.slug}`} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:image" content={article.image} />
      <meta property="og:url" content={`https://51chigua.com/article/${article.slug}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <main>
        <Navbar />

        {/* Article Header */}
        <section className="pt-24 pb-6 md:pb-8 bg-background-50">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mt-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500">
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                {article.category}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-6 md:py-10 bg-background-50">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                {/* Featured Image */}
                <div className="rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full aspect-[16/9] object-cover"
                  />
                </div>

                {/* Content */}
                <article
                  className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                    [&>p]:mb-5 [&>p]:leading-[1.8] [&>p]:text-[15px] md:[&>p]:text-base
                    [&>p]:text-foreground-700"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="mt-8 pt-6 border-t border-background-200/70">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <div className="mt-10">
                    <h3 className="font-heading text-lg text-foreground-950 mb-4">
                      相关推荐
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {relatedArticles.map((rel) => (
                        <ArticleCard key={rel.id} article={rel} variant="compact" />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-72 flex-shrink-0">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-heading text-base text-foreground-950 mb-4 flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-fire-line text-primary-500"></i>
                      </span>
                      热门文章
                    </h3>
                    <div className="space-y-4">
                      {hotArticles.map((hot) => (
                        <ArticleCard key={hot.id} article={hot} variant="compact" />
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}